import { useState } from "react";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  Page,
  Table,
  type Column,
  Badge,
  Button,
  FormModal,
  Input,
  Select,
  Text,
  Textarea,
  EmptyState,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useIsAdmin } from "../../auth/useAuth";
import {
  useCreateTemplate,
  useTemplateList,
  type Template,
  type TemplateCreate,
} from "../../lib/queries/templates";
import { RefreshButton } from "../../components/RefreshButton";

export function KindBadge({ kind }: { kind: Template["kind"] }) {
  return kind === "blocks" ? (
    <Badge variant="info">Blocks</Badge>
  ) : (
    <Badge variant="warn">Code</Badge>
  );
}

interface TemplateFormValues {
  key: string;
  name: string;
  channel: NonNullable<TemplateCreate["channel"]>;
  kind: TemplateCreate["kind"];
  description: string;
}

function CreateTemplateModal({ onClose }: { onClose: () => void }) {
  const create = useCreateTemplate();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<TemplateFormValues>({
    mode: "onTouched",
    defaultValues: {
      key: "",
      name: "",
      channel: "email",
      kind: "blocks",
      description: "",
    },
  });
  // An SMS is one text body: there is no HTML for the block renderer to build
  // and nothing for a theme to style, so the kind is forced and hidden.
  const channel = watch("channel");
  const isSMS = channel === "sms";

  async function onSubmit(values: TemplateFormValues) {
    try {
      await create.mutateAsync({
        key: values.key.trim(),
        name: values.name.trim(),
        channel: values.channel,
        kind: values.channel === "sms" ? "code" : values.kind,
        description: values.description.trim() || undefined,
      });
      notifySuccess("The template was created.");
      onClose();
    } catch (err) {
      apiErrorToFormErrors<TemplateFormValues>(err, setError, {
        fallbackMessage: "Creation failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="New template"
      onClose={onClose}
      busy={create.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Create"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="Key"
        required
        hint="Immutable identifier (lowercase letters, digits, hyphens) — used in the API."
        autoComplete="off"
        spellCheck={false}
        error={errors.key?.message}
        {...register("key", {
          required: "Required field",
          pattern: {
            value: /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/,
            message:
              "Only lowercase letters, digits and hyphens; must not start or end with a hyphen.",
          },
        })}
      />
      <Input
        label="Name"
        required
        error={errors.name?.message}
        {...register("name", { required: "Required field" })}
      />
      <Select
        label="Channel"
        hint="What a sender using this template sends. It must match the sender's."
        error={errors.channel?.message}
        {...register("channel")}
      >
        <option value="email">E-mail</option>
        <option value="sms">SMS</option>
      </Select>
      {!isSMS && (
        <Select
          label="Kind"
          hint="Blocks are composed in the visual editor; code is a hand-written HTML template."
          error={errors.kind?.message}
          {...register("kind")}
        >
          <option value="blocks">Blocks</option>
          <option value="code">Code</option>
        </Select>
      )}
      <Textarea
        label="Description"
        rows={3}
        error={errors.description?.message}
        {...register("description", {
          maxLength: { value: 512, message: "At most 512 characters" },
        })}
      />
    </FormModal>
  );
}

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["templates"]];

export function TemplatesPage() {
  const isAdmin = useIsAdmin();
  const { data, isLoading, error, refetch } = useTemplateList();
  const [createOpen, setCreateOpen] = useState(false);

  const columns: Column<Template>[] = [
    {
      key: "key",
      header: "Key",
      render: (row) => <Text variant="mono">{row.key}</Text>,
      linkTo: (row) => `/templates/${row.id}`,
    },
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (row) => row.name,
      linkTo: (row) => `/templates/${row.id}`,
    },
    {
      key: "kind",
      header: "Kind",
      render: (row) => <KindBadge kind={row.kind} />,
    },
    {
      key: "published",
      header: "Published",
      render: (row) =>
        row.has_published ? (
          <Badge weight="ghost" variant="ok" dot>Published</Badge>
        ) : (
          <Badge weight="ghost" variant="warn" dot>Draft only</Badge>
        ),
    },
    {
      key: "active",
      header: "Status",
      render: (row) =>
        row.active ? (
          <Badge weight="ghost" variant="ok" dot>Active</Badge>
        ) : (
          <Badge weight="ghost" variant="muted" dot>Inactive</Badge>
        ),
    },
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="Templates"
          subtitle="message content managed in one place"
          subtitleVariant="tagline"
          actions={
            <>
              <RefreshButton queryKeys={REFRESH_KEYS} />
              {isAdmin && (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus size={16} aria-hidden="true" />
                  Add template
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Table<Template>
            striped
            cards
            columns={columns}
            rows={data ?? []}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No templates"
                description="No template has been created yet."
              />
            }
          />
        </Page.Content>
      </Page>

      {createOpen && <CreateTemplateModal onClose={() => setCreateOpen(false)} />}
    </>
  );
}
